import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import IosShareIcon from "@mui/icons-material/IosShare";
import {
  Alert,
  Button,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Snackbar,
  ThemeProvider,
  createTheme,
  Stack,
  Typography
} from "@mui/material";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import * as React from "react";
import { useMemo } from "react";
import NoData from "../../../src/assets/images/NoData.svg";
import { DataFound, ExportButton } from "../../assets/css/styelComponent";
import SearchFilter from "../../utils/Search";
import Spinner from "../../utils/Spinner/Spinner";
import { months } from "../../utils/constants";
import { getYears } from "../../utils/helpers";
import AppDynamicComponents from "../AppComponents/AppDynamicComponents";

// Extracted styles to constants for better performance
const TABLE_STYLES = {
  container: {
    padding: 0,
    backgroundColor: "white",
    border: "1px solid rgba(54, 49, 87, 0.12)",
    borderRadius: "6px",
  },
  header: {
    paddingLeft: "24px",
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "59px",
    paddingRight: "12px",
    borderTopLeftRadius: "6px",
    borderTopRightRadius: "6px",
  },
  title: {
    fontWeight: 500,
    fontSize: "14px",
  },
  button: {
    backgroundColor: "#BE8976",
    borderRadius: "5px",
  },
  tableContainer: {
    borderBottomLeftRadius: "6px",
    borderBottomRightRadius: "6px",
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    overflowX: "auto",
  },
  table: {
    minWidth: 900,
    width: "100%",
  },
  tableHead: {
    backgroundColor: "#BE89763D",
  },
  tableCell: {
    fontSize: "14px",
    fontWeight: "500",
  },
  tableBodyCell: {
    fontSize: "14px",
    color: "#566166",
    fontWeight: "500",
    whiteSpace: "nowrap",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    borderRadius: "4px",
    border: "1px solid #ECEBEB",
    background: "#FFF",
    margin: "25px",
    padding: "25px",
  },
  totalCount: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0px 10px",
  },
  totalCountLabel: {
    color: "#363157",
    margin: "0px",
    fontSize: "14px",
    fontWeight: "400",
  },
  formControl: {
    width: "100%",
  },
  select: {
    height: "44px",
    fontSize: "14px",
    color: "rgb(86, 97, 102)",
    fontWeight: "400",
  },
  selectStyles: {
    "& .MuiInputLabel-root": {
      color: "#363157",
      "&.Mui-focused": {
        color: "#BE8976",
      },
    },
    "&.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#BE8976",
      },
    },
  },
  menuItem: {
    fontSize: "14px",
    color: "rgb(86, 97, 102)",
    fontWeight: "400",
  },
  menuItemSelected: {
    background: "#BE8976 !important",
    color: "#fff !important",
  },
  menuProps: {
    PaperProps: {
      style: {
        maxHeight: 210,
      },
    },
  },
  divider: {
    height: 75,
    mx: 2,
  },
  noData: {
    margin: "24.5px auto",
  },
  exportIcon: {
    marginBottom: '5px'
  },
  startIcon: {
    marginBottom: '3px'
  }
};

const AppTable = React.memo(function AppTable(props) {
  const {
    columns,
    title,
    isSearch,
    isAddCustomer,
    handleAddCustomerClick,
    tableData = { data: [], page: 1, totalCount: 0 },
    spinner,
    renderPageNavigator,
    isMonthYear,
    isStatus,
    statusOption,
    selectRole,
    isdelete,
    isSnackOpen,
    handleCloseSnack,
    handleExport,
    severity,
    isReassign,
    handleOpenReassignDialog,
    isAddEmployee,
    handleOpenAddDialog,
    isAddProduct,
    handleAddProductClick,
    isOnboardClient,
    handleOnboardClientClick,
    isAddTask,
    handleAddTaskClick,
    isRole,
    roles,
    isAddHoliday,
    handleFilterChange,
    filterState = {},
    isQuickSearch,
    searchPlaceholder,
    dynamicFilters,
    ismonthYear,
    pageSize,
    searchWidth = 3,
    exportText = "Export Portfolio",
    exportDisabled = false,
  } = props;

  const { search, month, year, role, page } = filterState;

  // Memoize years to prevent unnecessary recalculations
  const years = useMemo(() => getYears(), []);

  // Memoize user role check
  const userRole = useMemo(() => {
    try {
      return JSON.parse(window.localStorage.getItem("userProduct"))?.data?.role;
    } catch {
      return null;
    }
  }, []);

  const shouldShowAddCustomer = useMemo(() => {
    return !["RM/RMA", "OPS", "ADMIN"].includes(userRole);
  }, [userRole]);

  const shouldShowExportButton = useMemo(()=>{
    return ["SRM", "PRODUCT_TEAM", "SERVICE & OPS", "SUPERADMIN", "RM/RMA"].includes(userRole)
  }, [userRole])

  const renderActionButtons = () => (
    <Box display="flex" alignItems="center" gap={2}>
      {shouldShowExportButton && handleExport && (
          <ExportButton
            variant="contained"
            startIcon={<IosShareIcon style={TABLE_STYLES.exportIcon} />}
            onClick={handleExport}
            disabled={exportDisabled}
          >
            {exportText}
          </ExportButton>
      )}
      {isAddProduct && (
        <Button
          variant="contained"
          startIcon={<AddCircleOutlinedIcon style={TABLE_STYLES.startIcon} />}
          style={TABLE_STYLES.button}
          onClick={handleAddProductClick}
        >
          ADD PRODUCT
        </Button>
      )}
      {isAddCustomer && (
        <Button
          variant="contained"
          startIcon={<AddCircleOutlinedIcon style={TABLE_STYLES.startIcon} />}
          style={{
            ...TABLE_STYLES.button,
            display: shouldShowAddCustomer ? "flex" : "none",
          }}
          onClick={handleAddCustomerClick}
        >
          ADD CUSTOMER
        </Button>
      )}
      {isOnboardClient && (
        <Button
          variant="contained"
          startIcon={<AddCircleOutlinedIcon style={TABLE_STYLES.startIcon} />}
          style={TABLE_STYLES.button}
          onClick={handleOnboardClientClick}
        >
          ONBOARD CLIENT
        </Button>
      )}
      {isAddEmployee && (
        <Button
          variant="contained"
          startIcon={<AddCircleOutlinedIcon style={TABLE_STYLES.startIcon} />}
          style={TABLE_STYLES.button}
          onClick={handleOpenAddDialog}
        >
          ADD EMPLOYEE
        </Button>
      )}
      {isAddTask && (
        <Button
          variant="contained"
          startIcon={<AddCircleOutlinedIcon style={TABLE_STYLES.startIcon} />}
          style={TABLE_STYLES.button}
          onClick={handleAddTaskClick}
        >
          ADD TASK
        </Button>
      )}
      {isAddHoliday && (
        <Button
          variant="contained"
          startIcon={<AddCircleOutlinedIcon style={TABLE_STYLES.startIcon} />}
          style={TABLE_STYLES.button}
          onClick={handleOpenAddDialog}
        >
          ADD HOLIDAY
        </Button>
      )}
      {isReassign && (
        <Button
          variant="contained"
          style={{ backgroundColor: "#BE8976" }}
          onClick={handleOpenReassignDialog}
        >
          Re-assign
        </Button>
      )}
    </Box>
  );

  const renderFilters = () => (
    <Grid container alignItems="center" sx={{ px: 3 }}>
      {isSearch && (
        <Grid item xs={ismonthYear ? 5 : searchWidth} display="flex" alignItems="center">
          <SearchFilter
            quickSearch
            handleSearch={handleFilterChange}
            setValue={search}
            placeHolder={searchPlaceholder}
          />
          {props.filterDropdown}
          <Divider orientation="vertical" flexItem sx={TABLE_STYLES.divider} />
        </Grid>
      )}

      {isMonthYear && (
        <Grid item xs={4} display="flex" alignItems="center">
          <FormControl sx={TABLE_STYLES.formControl}>
            <InputLabel style={TABLE_STYLES.menuItem}>Month</InputLabel>
            <Select
              value={month}
              name="month"
              onChange={handleFilterChange}
              label="Month"
              style={TABLE_STYLES.select}
              sx={TABLE_STYLES.selectStyles}
              MenuProps={TABLE_STYLES.menuProps}
            >
              {months?.map((monthOption, index) => (
                <MenuItem
                  style={TABLE_STYLES.menuItem}
                  key={`month-${index}`}
                  value={monthOption}
                  sx={{
                    "&.Mui-selected": TABLE_STYLES.menuItemSelected,
                  }}
                >
                  {monthOption}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ ...TABLE_STYLES.formControl, ml: 2 }}>
            <InputLabel style={TABLE_STYLES.menuItem}>Year</InputLabel>
            <Select
              value={year}
              name="year"
              onChange={handleFilterChange}
              label="Year"
              style={TABLE_STYLES.select}
              sx={TABLE_STYLES.selectStyles}
              MenuProps={TABLE_STYLES.menuProps}
            >
              {years?.map((yearOption, index) => (
                <MenuItem
                  style={TABLE_STYLES.menuItem}
                  key={`year-${index}`}
                  value={yearOption}
                  sx={{
                    "&.Mui-selected": TABLE_STYLES.menuItemSelected,
                  }}
                >
                  {yearOption}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider orientation="vertical" flexItem sx={TABLE_STYLES.divider} />
        </Grid>
      )}

      {isRole && (
        <Grid item xs={4} display="flex" alignItems="center">
          <FormControl sx={{ ...TABLE_STYLES.formControl, mx: 2 }}>
            <InputLabel style={TABLE_STYLES.menuItem}>Role Name</InputLabel>
            <Select
              name="role"
              value={role}
              label="Role Name"
              style={TABLE_STYLES.select}
              sx={TABLE_STYLES.selectStyles}
              onChange={handleFilterChange}
            >
              {roles.map((roleOption, index) => (
                <MenuItem
                  style={TABLE_STYLES.menuItem}
                  sx={{
                    "&.Mui-selected": TABLE_STYLES.menuItemSelected,
                  }}
                  key={`role-${index}`}
                  value={roleOption}
                >
                  {roleOption}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider orientation="vertical" flexItem sx={TABLE_STYLES.divider} />
        </Grid>
      )}

      <Grid item xs={9} container spacing={2}>
        {dynamicFilters?.map((filter, index) => (
          <Grid item xs={filter?.isOnboardStatus ? 3 : 2.3} key={`filter-${index}`}>
            <AppDynamicComponents
              type={filter.type}
              name={filter.name}
              label={filter.label}
              onChange={handleFilterChange}
              onBlur={handleFilterChange}
              value={filter.value}
              options={filter.options}
              disableClearable={filter.disableClearable}
            />
          </Grid>
        ))}
      </Grid>

      
    </Grid>
  );

  const renderTableContent = () => (
    <TableContainer component={Paper} sx={TABLE_STYLES.tableContainer}>
      <Table sx={TABLE_STYLES.table} aria-label="custom pagination table">
        <TableHead>
          <TableRow
            style={TABLE_STYLES.tableHead}
            className="tableinnerrow-head"
          >
            {columns.map((col, idx) => (
              <TableCell
                key={`header-${idx}`}
                align={col.align || "left"}
                width={col.width}
                style={TABLE_STYLES.tableCell}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {tableData?.data && tableData.data.length > 0 && (
          <TableBody className="tabel-data-padding">
            {tableData?.data.map((row, rowIndex) => (
              <TableRow key={`row-${rowIndex}`}>
                {columns.map((col, colIndex) => (
                  <TableCell
                    key={`cell-${rowIndex}-${colIndex}`}
                    align={col.align || "left"}
                    style={TABLE_STYLES.tableBodyCell}
                  >
                    {col.render
                      ? col.render(row, rowIndex)
                      : col.field === "sno"
                      ? page * 10 - 10 + (rowIndex + 1)
                      : row[col.field] ?? "-"}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        )}
      </Table>
      {spinner ? (
        <Spinner />
      ) : Object.keys(tableData).length !== 0 &&
        tableData?.data?.length !== 0 ? (
        <Grid style={TABLE_STYLES.footer}>
          <div style={TABLE_STYLES.totalCount}>
            <label style={TABLE_STYLES.totalCountLabel}>
              {`Showing ${(page - 1) * 10 + 1} to ${
                (page - 1) * 10 + 1 + tableData.data?.length - 1
              } of ${tableData.totalCount} records`}
            </label>
          </div>
          <div>{renderPageNavigator()}</div>
        </Grid>
      ) : (
        <DataFound style={TABLE_STYLES.noData}>
          <img src={NoData} alt="No data" />
          {"Data Not Found..."}
        </DataFound>
      )}
    </TableContainer>
  );

  return (
    <Box sx={TABLE_STYLES.container}>
      <Box sx={TABLE_STYLES.header}>
        <span style={TABLE_STYLES.title}>{title}</span>
  
        
      
        {renderActionButtons()}
      </Box>
      <Divider />
      {renderFilters()}
      {renderTableContent()}
      {isSnackOpen && (
        <Snackbar
          open={isSnackOpen}
          autoHideDuration={3000}
          onClose={handleCloseSnack}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            sx={{ fontWeight: 600 }}
            onClose={handleCloseSnack}
            severity={severity}
            variant="filled"
          >
            {isdelete}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
});

export default AppTable;
